import { z } from "zod";
import {
  EmbedFrame,
  WidgetEmpty,
  isSafeEmbedUrl,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";

/**
 * `embed` — an "Embedded website" card: renders any external http(s) page inside the
 * library's hardened, sandboxed `EmbedFrame`. Presentational (no data hook / `.graphql`);
 * the URL is set via the gear settings dialog (a `text` field), NOT inline — so this card
 * exercises the declarative-`settings` path (the note card exercises inline editing). Add
 * as many as you like — each instance carries its own `config.url`.
 */
export const embedConfigSchema = z
  .looseObject({ url: z.string().default("") });

export type EmbedConfig = z.infer<typeof embedConfigSchema>;

function EmbedRender({ config }: WidgetRenderContext<EmbedConfig>) {
  const url = config.url.trim();
  // `isSafeEmbedUrl` rejects empty/malformed/non-http(s)/self-origin — anything the
  // frame won't (or shouldn't) load — so this one gate also covers the "not set yet" case.
  if (!isSafeEmbedUrl(url)) {
    return (
      <WidgetEmpty
        title="No website yet"
        description="Paste a link in Settings."
      />
    );
  }
  // `title` is the iframe's a11y name (screen readers); the URL describes the content.
  return <EmbedFrame src={url} title={url} />;
}

const embedSettings: WidgetSettingsGroup[] = [
  {
    title: "Website",
    fields: [
      {
        key: "url",
        kind: "text",
        label: "URL",
        placeholder: "https://www.youtube.com/watch?v=…",
        description:
          "Paste any link — YouTube, Vimeo, Google Docs, Spotify & more are auto-converted to embeds. Some sites (Google, X, banks) block embedding; use the ↗ button to open them in a new tab.",
      },
    ],
  },
];

export const embedDef: WidgetModule<EmbedConfig> = {
  type: "embed",
  title: "Website",
  icon: "globe",
  span: { minW: 2, minH: 3, maxW: 12, maxH: 12, defaultW: 5 },
  configSchema: embedConfigSchema,
  settings: embedSettings,
  catalog: [
    {
      key: "embed.default",
      label: "Custom embed",
      description: "Embed a web page or video (YouTube, Vimeo, Google Docs…) in a sandboxed frame.",
      section: "embeds" satisfies WidgetSection,
      // Embeds are named by the user, not their gallery label (U2).
      autoTitle: false,
      tags: ["external"] satisfies readonly WidgetTag[],
      defaultConfig: { url: "" },
      defaultW: 5,
    },
    {
      key: "embed.youtube",
      label: "YouTube",
      description: "Embed a YouTube video — paste the watch link in the card's settings.",
      section: "embeds" satisfies WidgetSection,
      // Embeds are named by the user, not their gallery label (U2).
      autoTitle: false,
      icon: "play-circle",
      tags: ["external"] satisfies readonly WidgetTag[],
      defaultConfig: { url: "" },
      defaultW: 5,
    },
  ],
  render: EmbedRender,
};
